import cloneDeep from "lodash.clonedeep";

/**
 * Finds a product parameter anywhere in the template tree (sections → subSections → parameters, including nested childsParameters).
 * Used so rapid UI updates can read the latest valuesConfigs before building the next PUT payload.
 */
export function findParameterByIdInTemplate(
  root: any,
  parameterId: string
): any | null {
  if (!root || !parameterId) return null;

  const searchInParameterList = (list: any[]): any | null => {
    if (!Array.isArray(list)) return null;
    for (const p of list) {
      if (!p) continue;
      if (p.id === parameterId) return p;
      const inChilds = searchInParameterList(p.childsParameters || []);
      if (inChilds) return inChilds;
    }
    return null;
  };

  const found = searchInParameterList(root.parameters);
  if (found) return found;

  if (Array.isArray(root.sections)) {
    for (const s of root.sections) {
      const f = findParameterByIdInTemplate(s, parameterId);
      if (f) return f;
    }
  }
  if (Array.isArray(root.subSections)) {
    for (const ss of root.subSections) {
      const f = findParameterByIdInTemplate(ss, parameterId);
      if (f) return f;
    }
  }
  return null;
}

/**
 * Returns a deep-cloned template with the given parameter's valuesConfigs replaced.
 * Backend: update-product-parameter is assumed to merge by ids; the products-settings widget already sends partial parameter DTOs successfully.
 */
export function mergeParameterValuesConfigsInTemplate(
  templateRoot: any,
  parameterId: string,
  valuesConfigs: any[]
): any {
  if (!templateRoot || !parameterId) return templateRoot;
  const next = cloneDeep(templateRoot);

  const patchInParameterList = (list: any[]): boolean => {
    if (!Array.isArray(list)) return false;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      if (!p) continue;
      if (p.id === parameterId) {
        list[i] = {
          ...p,
          valuesConfigs: valuesConfigs.map((vc) => ({ ...vc })),
        };
        return true;
      }
      if (patchInParameterList(p.childsParameters || [])) return true;
    }
    return false;
  };

  const walk = (node: any): boolean => {
    if (patchInParameterList(node.parameters || [])) return true;
    if (Array.isArray(node.sections)) {
      for (const s of node.sections) {
        if (walk(s)) return true;
      }
    }
    if (Array.isArray(node.subSections)) {
      for (const ss of node.subSections) {
        if (walk(ss)) return true;
      }
    }
    return false;
  };

  walk(next);
  return next;
}
